"use client";

import AppleOAuthButton from "@/components/auth/AppleOAuthButton";
import GoogleOAuthButton from "@/components/auth/GoogleOAuthButton";
import SmartEmailPhoneInput from "@/components/auth/SmartEmailPhoneInput";
import MasterButton from "@/components/common/masterButton";
import useToast from "@/hooks/useToast";
import {
  useAuthStore,
  usePendingCircleInviteOwnerUserIdSelector,
} from "@/store/useAuthStore";
import { signIn, socialSignIn } from "@/utils/api";
import { setCookie } from "@/utils/cookie";
import { facebook, loginBg, logoSmall } from "@/utils/image";
import { validationPatterns } from "@/utils/validations";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { useMutation } from "@tanstack/react-query";
import { useFormik } from "formik";
import { isValidPhoneNumber } from "libphonenumber-js";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import { FiEye, FiEyeOff } from "react-icons/fi";
import * as Yup from "yup";
import { getFCMToken, getStoredFCMToken } from "@/utils/firebase";
import { acceptPendingCircleInvite } from "@/utils/circleInvite";

const initialValues = {
  emailOrPhone: "",
  password: "",
};

const validationSchema = Yup.object({
  emailOrPhone: Yup.string()
    .trim()
    .required("Email or phone number is required")
    .test("email-or-phone", "Invalid email or phone number", function (value) {
      if (!value) return false;

      // Check if it's an email
      if (value.includes("@")) {
        return validationPatterns.email.test(value);
      }

      // Check if it's a valid phone number
      try {
        return isValidPhoneNumber(value);
      } catch {
        return false;
      }
    }),
  password: Yup.string().required("Password is required"),
});

export default function LoginForm() {
  const router = useRouter();
  const { setEmail, clearEmail, setOtpFlow, clearOtpFlow, setUser } =
    useAuthStore();
  const pendingInviteOwnerId = usePendingCircleInviteOwnerUserIdSelector();
  const isInvited = !!pendingInviteOwnerId;
  const { showToast } = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const [fcmToken, setFcmToken] = useState(() => getStoredFCMToken());

  // Get FCM token on mount
  useEffect(() => {
    getFCMToken().then((token) => {
      if (token) setFcmToken(token);
    });
  }, []);

  // Clear any existing email from previous forgot-password sessions
  useEffect(() => {
    clearEmail();
    clearOtpFlow();
  }, [clearEmail, clearOtpFlow]);

  // Initialize formik
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      const { emailOrPhone, password } = values;

      // Determine if it's email or phone
      const payload = emailOrPhone.includes("@")
        ? { email: emailOrPhone?.toLocaleLowerCase()?.trim(), password }
        : { phone: emailOrPhone, password };

      mutate(payload);
    },
  });

  const { handleSubmit, handleChange, values, errors, touched } = formik;

  // Mutation for login
  const { mutate, isPending } = useMutation({
    mutationFn: (values) => signIn(values),
    onSuccess: (data, variables) => {
      if (data?.success) {
        // Store email/phone and flow type in Zustand
        const identifier = variables.email || variables.phone;
        setEmail(identifier);
        setOtpFlow("login");

        router.push("/otp");

        showToast({
          message: data?.message || "Login successful!",
          type: "success",
        });
      }
    },
  });

  // Social Sign-In Mutation
  const { mutate: socialSignInMutate, isPending: isSocialSignInPending } =
    useMutation({
      mutationFn: (socialData) =>
        socialSignIn({ ...socialData, ...(fcmToken ? { fcmToken } : {}) }),
      onSuccess: (data) => {
        console.log("Social sign-in data:", data);
        if (data?.success) {
          setCookie("access_token", data.data.tokens.accessToken);
          if (data.data.user?.role) setCookie("user_role", data.data.user.role);
          setUser(data.data.user);

          const finishSocialLogin = async () => {
            try {
              const inviteResponse = await acceptPendingCircleInvite();
              if (inviteResponse?.joined) {
                showToast({
                  message:
                    inviteResponse.message ||
                    "Circle invite accepted successfully!",
                  type: "success",
                });
              }
            } catch (error) {
              showToast({
                message:
                  error?.response?.data?.message ||
                  "Logged in successfully, but circle join failed.",
                type: "error",
              });
            }

            if (!data.data.user.termsAndPrivacyAccepted) {
              setCookie("terms_accepted", "false");
              router.replace("/privacy");
            } else {
              setCookie("terms_accepted", "true");
              router.replace("/dashboard");
            }
          };

          finishSocialLogin();

          showToast({
            message: data?.message || "Logged in successfully!",
            type: "success",
          });
        }
      },
    });

  const handleOAuthError = (error) => {
    console.log("OAuth error:", error);
    showToast({
      message: "Authentication failed. Please try again.",
      type: "error",
    });
  };

  return (
    <div className="position-relative commonAuthOuter">
      {isSocialSignInPending && (
        <div className="loading-spinner">
          <div
            className="spinner-border spinnner-pink"
            role="status"
            style={{ width: "3rem", height: "3rem" }}
          >
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 fw-semibold text-sm">Signing you in...</p>
        </div>
      )}

      <div className="d-flex justify-content-center  m_bottom39">
        <Link href="/">
          <Image src={logoSmall} alt="logo" />
        </Link>
      </div>

      <Container
        fluid
        className="d-flex justify-content-center align-items-start"
      >
        <div
          className="login_Card"
          style={{ maxWidth: "486px", width: "100%" }}
        >
          <h3 className="text-center mb-4 heading24 black fw-bold">
            {isInvited ? "Sign in to join the circle" : "Welcome back"}
          </h3>

          <form onSubmit={handleSubmit}>
            {/* Smart Email/Phone Field */}
            <div className="position-relative mb-4">
              <SmartEmailPhoneInput
                value={values.emailOrPhone}
                onChange={(value) =>
                  formik.setFieldValue("emailOrPhone", value)
                }
                error={touched.emailOrPhone && errors.emailOrPhone}
                placeholder="Email or phone no"
                label="Email or Phone no"
              />
            </div>

            {/* Password Field */}
            <div className="mb-2">
              <div className="position-relative">
                <label className="customLabel">Password</label>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  className="form-control icon_input"
                  placeholder="Enter your password"
                  onChange={handleChange}
                  value={values.password}
                />
                <div
                  onClick={() => setShowPassword(!showPassword)}
                  className="eye_icon"
                >
                  {!showPassword ? (
                    <FiEyeOff className="grey" size={18} />
                  ) : (
                    <FiEye className="grey" size={18} />
                  )}
                </div>
              </div>
              {errors.password && touched.password && (
                <div className="text-danger small mt-1">{errors.password}</div>
              )}
            </div>

            {/* Forgot Password */}
            <div className="text-end my-3">
              <Link href="/forgot-password" className="forgot_link">
                Forgot your password?
              </Link>
            </div>

            {/* Login Button */}
            <MasterButton
              disabled={isPending}
              buttonName={isPending ? "Signing in..." : "Sign in"}
              type="submit"
              width="w-100"
            />
          </form>

          {/* Bottom Links */}
          <div className="text-center mt-4">
            <p className="mb-0 dontAcc">
              Don't have an account?{" "}
              <Link href="/signup" className="forgot_link">
                Free Signup
              </Link>
            </p>
          </div>
          <div className="social-auth-wrapper text-center my-4">
            <div className="divider">
              <span>Or sign up with</span>
            </div>

            <div className="d-flex justify-content-center gap-3 my-3">
              {/* Apple OAuth */}
              <AppleOAuthButton
                onSuccess={socialSignInMutate}
                onError={handleOAuthError}
              />

              {/* Google OAuth */}
              <GoogleOAuthProvider
                clientId={process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID || ""}
              >
                <GoogleOAuthButton
                  onSuccess={socialSignInMutate}
                  onError={handleOAuthError}
                />
              </GoogleOAuthProvider>

              {/* Facebook OAuth */}
              {/* <button className="social-btn facebook" type="button" disabled>
                <Image src={facebook} alt="facebook" width={20} height={20} />
              </button> */}
            </div>
            <Link href="/how-it-works" className="forgot_link how_it_works">
              How it works
            </Link>
          </div>
        </div>
      </Container>
    </div>
  );
}
